"use client";

import { useState, useEffect } from "react";
import AppImage from "@/components/ui/AppImage";

const navLinks = [
  { label: "About", href: "#about" },
  { label: "Services", href: "#services" },
  { label: "All Services", href: "/services", external: true },
  { label: "Ports", href: "#ports" },
  { label: "Clients", href: "#testimonials" },
  { label: "Contact", href: "#contact" },
];

export default function NavBar() {
  const [scrolled, setScrolled] = useState(false);
  const [mobileOpen, setMobileOpen] = useState(false);
  const [activeSection, setActiveSection] = useState("hero");

  useEffect(() => {
    const handleScroll = () => {
      setScrolled(window.scrollY > 60);
    };
    window.addEventListener("scroll", handleScroll, { passive: true });
    return () => window.removeEventListener("scroll", handleScroll);
  }, []);

  // Close mobile menu on resize
  useEffect(() => {
    const handleResize = () => { if (window.innerWidth >= 768) setMobileOpen(false); };
    window.addEventListener("resize", handleResize);
    return () => window.removeEventListener("resize", handleResize);
  }, []);

  const handleNavClick = (href: string) => {
    setMobileOpen(false);
    const id = href.replace("#", "");
    const el = document.getElementById(id);
    if (el) el.scrollIntoView({ behavior: "smooth" });
  };

  return (
    <>
      <nav
        className={`fixed top-0 left-0 right-0 z-50 transition-all duration-500 ${
          scrolled
            ? "py-3 bg-navy-900/95 backdrop-blur-xl border-b border-white/5 shadow-lg"
            : "py-5"
        }`}
      >
        <div className="max-w-7xl mx-auto px-6 md:px-10 flex items-center justify-between">
          {/* Logo */}
          <a
            href="#hero"
            onClick={(e) => { e.preventDefault(); window.scrollTo({ top: 0, behavior: "smooth" }); }}
            className="flex items-center"
          >
            <AppImage
              src="/assets/images/logo-white-1-1772521738205.png"
              alt="BlueSea Marine Services logo"
              width={160}
              height={62}
              className="h-10 w-auto object-contain"
              priority
            />
          </a>

          {/* Desktop links */}
          <div className="hidden md:flex items-center gap-8">
            {navLinks.map((link) => (
              link.external ? (
                <a
                  key={link.href}
                  href={link.href}
                  className="text-[12px] uppercase tracking-[0.25em] font-bold text-ocean-400 hover:text-white transition-colors duration-300"
                >
                  {link.label}
                </a>
              ) : (
              <a
                key={link.href}
                href={link.href}
                onClick={(e) => { e.preventDefault(); handleNavClick(link.href); }}
                className="text-[12px] uppercase tracking-[0.25em] font-bold text-[#8BA3BC] hover:text-white transition-colors duration-300"
              >
                {link.label}
              </a>
              )
            ))}
          </div>

          {/* CTA + Hamburger */}
          <div className="flex items-center gap-4">
            <a
              href="#contact"
              onClick={(e) => { e.preventDefault(); handleNavClick("#contact"); }}
              className="hidden md:inline-flex items-center gap-2 px-6 py-2.5 rounded-full bg-ocean-600 hover:bg-ocean-500 text-white text-[12px] font-bold uppercase tracking-widest transition-all duration-300 hover:-translate-y-0.5"
            >
              Get Quote
            </a>
            <button
              onClick={() => setMobileOpen(!mobileOpen)}
              className="md:hidden flex flex-col gap-1.5 w-8 group"
              aria-label="Toggle mobile menu"
            >
              <span className={`w-full h-0.5 bg-white transition-all duration-300 ${mobileOpen ? "rotate-45 translate-y-2" : ""}`} />
              <span className={`h-0.5 bg-white transition-all duration-300 ${mobileOpen ? "opacity-0 w-0" : "w-2/3"}`} />
              <span className={`w-full h-0.5 bg-white transition-all duration-300 ${mobileOpen ? "-rotate-45 -translate-y-2" : ""}`} />
            </button>
          </div>
        </div>
      </nav>

      {/* Mobile menu */}
      <div
        className={`fixed inset-0 z-40 transition-all duration-500 ${
          mobileOpen ? "opacity-100 pointer-events-auto" : "opacity-0 pointer-events-none"
        }`}
      >
        <div className="absolute inset-0 bg-navy-900/98 backdrop-blur-xl" onClick={() => setMobileOpen(false)} />
        <div
          className={`absolute top-0 right-0 h-full w-[80vw] max-w-sm bg-navy-800 border-l border-white/5 p-8 flex flex-col gap-8 transition-transform duration-500 ${
            mobileOpen ? "translate-x-0" : "translate-x-full"
          }`}
        >
          <div className="flex items-center justify-between pt-4">
            <AppImage
              src="/assets/images/logo-white-1-1772521738205.png"
              alt="BlueSea Marine Services logo"
              width={140}
              height={54}
              className="h-9 w-auto object-contain"
            />
            <button onClick={() => setMobileOpen(false)} className="text-[#8BA3BC] hover:text-white transition-colors" aria-label="Close menu">
              <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                <line x1="18" y1="6" x2="6" y2="18"/><line x1="6" y1="6" x2="18" y2="18"/>
              </svg>
            </button>
          </div>

          <div className="flex flex-col gap-2 mt-4">
            {navLinks.map((link, i) => (
              link.external ? (
                <a
                  key={link.href}
                  href={link.href}
                  className="flex items-center gap-4 px-4 py-4 rounded-xl text-ocean-400 hover:text-white hover:bg-white/5 transition-all duration-300 text-[15px] font-bold uppercase tracking-widest"
                  onClick={() => setMobileOpen(false)}
                >
                  <span className="text-[11px] text-ocean-400/60 font-mono">0{i + 1}</span>
                  {link.label}
                </a>
              ) : (
              <a
                key={link.href}
                href={link.href}
                onClick={(e) => { e.preventDefault(); handleNavClick(link.href); }}
                className="flex items-center gap-4 px-4 py-4 rounded-xl text-[#8BA3BC] hover:text-white hover:bg-white/5 transition-all duration-300 text-[15px] font-bold uppercase tracking-widest"
              >
                <span className="text-[11px] text-ocean-400/60 font-mono">0{i + 1}</span>
                {link.label}
              </a>
              )
            ))}
          </div>

          <div className="mt-auto space-y-4">
            <a
              href="tel:+912249642412"
              className="flex items-center gap-3 text-[#8BA3BC] text-[14px] hover:text-white transition-colors"
            >
              <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                <path d="M22 16.92v3a2 2 0 0 1-2.18 2 19.79 19.79 0 0 1-8.63-3.07A19.5 19.5 0 0 1 4.69 13.5a19.79 19.79 0 0 1-3.07-8.67A2 2 0 0 1 3.6 2.69h3a2 2 0 0 1 2 1.72c.127.96.361 1.903.7 2.81a2 2 0 0 1-.45 2.11L7.91 10.4a16 16 0 0 0 5.69 5.69l.88-.88a2 2 0 0 1 2.11-.45c.907.339 1.85.573 2.81.7A2 2 0 0 1 21.1 17.5l-.18-.58z"/>
              </svg>
              +91 22 – 49642412
            </a>
            <a
              href="#contact"
              onClick={(e) => { e.preventDefault(); handleNavClick("#contact"); setMobileOpen(false); }}
              className="w-full flex items-center justify-center gap-2 px-6 py-4 rounded-2xl bg-ocean-600 hover:bg-ocean-500 text-white font-bold uppercase tracking-widest text-[13px] transition-all"
            >
              Request Service
            </a>
          </div>
        </div>
      </div>
    </>
  );
}